import './home.css';

import { useEffect, useRef, useState } from 'react';
import MetaIdbRepository from '../../repositories/idb/MetaIdbRepository';
import Overlay from '../../components/layout/overlay/Overlay';
import { isObjectNull, isNullOrEmpty } from '../../components/helpers/ObjectHelpers';
import { getHomeWhoAmI, isLoggedIn } from '../../components/helpers/AuthHelpers';
import PushPermissionDialog from '../pilot/pushNotifications/PushPermissionModal';
import { refreshDeviceKey, updateSubscriptionTokenThenSetRefreshed } from '../pilot/pushNotifications/PushHelpers';
import ContentGrid from '../../components/layout/ContentGrid';
import { HomeGuestDepartment } from './components/HomeGuestDepartment';
import { HomeDashboard } from './components/HomeDashboard';
import { ApplicationRightSystemNames } from 'components/constants/ApplicationRightSystemNames';
import { ApplicationRightService } from 'services/ApplicationRightService';
import { useApplicationRights, useConfiguration, useSetHeaderContent, useWarningNotification } from '../../components/hooks/useAppStore';

const initialState = {
    isBusy: true,
    hasErrors: false,
    name: "",
    showPushPermissionsDialog: false,
};

export const Index = () => {
    const [
        {
            isBusy,
            hasErrors,
            name,
            showPushPermissionsDialog
        }, setState] = useState(initialState);

    const applicationRights = useApplicationRights();
    const configuration = useConfiguration();
    const updateHeaderContent = useSetHeaderContent();
    const addWarningNotification = useWarningNotification();

    const dashboardRef = useRef();
    const guestDepartmentRef = useRef(null);

    useEffect(() => {
        if (!applicationRights || !configuration) return;
        initializeAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationRights, configuration]);

    // Initialize function
    useEffect(() => {
        updateHeaderContent("NjordPilotWeb", "", false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handlePushPermissionDialogClose() {
        localStorage.setItem("shownAlready", "1");
        setState((prev) => ({
            ...prev,
            showPushPermissionsDialog: false
        }));
    }

    return (
        <>
            {!isBusy && (
                <>
                    <HomeContentGrid name={name} onPullToRefresh={onPullToRefresh}>
                        {showPushPermissionsDialog && <PushPermissionDialog onClose={handlePushPermissionDialogClose} />}
                        <HomeGuestDepartment ref={guestDepartmentRef} />

                        <HomeDashboard
                            ref={dashboardRef}
                            applicationRights={applicationRights}
                            />
                    </HomeContentGrid>
                </>
            )}
            <Overlay isBusy={isBusy} onReloadClick={onReloadClick} hasErrors={hasErrors} />
        </>
    );

    function onReloadClick() {
        setState((prev) => ({
            ...prev,
            hasErrors: false,
            isBusy: true
        }));

        initializeOnlineAsync();
    }

    async function initializeAsync() {
        if (navigator.onLine) {
            initializeOnlineAsync();
        } else {
            initializeOfflineAsync();
        }
    }

    async function initializeOnlineAsync() {

        if (!isLoggedIn()) {
            setState((prev) => ({
                ...prev,
                isBusy: false
            }));
        } else {
            const deviceId = localStorage.getItem("deviceId");
            if (deviceId) {
                refreshDeviceKey(deviceId);
                updateSubscriptionTokenThenSetRefreshed();
            }

            const name = await getHomeWhoAmI(configuration);

            setState((prev) => ({
                ...prev,
                isBusy: false,
                applicationRights: applicationRights,
                name: name,
                showPushPermissionsDialog: checkPushPermissionDialogVisibility(applicationRights)
            }));
        }
    }

    async function initializeOfflineAsync(applicationRights) {

        if (isLoggedIn() === false) {
            setState((prev) => ({
                ...prev,
                isBusy: false
            }));

            //for push notifications
            localStorage.removeItem("refreshed")
            if (window?.Notification?.permission !== "granted") {
                localStorage.removeItem("shownAlready")
            }

        } else {
            const whoAmI = await MetaIdbRepository.getWhoAmIAsync();
            let firstName = "";

            if (!isObjectNull(whoAmI)) {
                firstName = whoAmI.firstName;
            } else {
                addWarningNotification("Kunne ikke laste inn person informasjon");
            }
            setState((prev) => ({
                ...prev,
                name: firstName,
                isBusy: false,
                hasErrors: false,
                applicationRights: applicationRights
            }));
        }
    }

    function onPullToRefresh() {
        guestDepartmentRef.current?.onRefresh();
        dashboardRef.current.onRefresh();
    }
}

const HomeContentGrid = ({ children, name, onPullToRefresh }) => {
    return (
        <ContentGrid
            title={!isNullOrEmpty(name) ? "Velkommen" : ""}
            subTitle={name}
            titleIcon="lighthouse"
            onPullToRefresh={onPullToRefresh}>
            <div className="home-content-grid">
                {children}
            </div>
        </ContentGrid>
    )
}

function checkPushPermissionDialogVisibility(applicationRights) {
    const applicationRightsService = new ApplicationRightService(applicationRights);
    const canAccessPreferences = applicationRightsService.canAccess([
        ApplicationRightSystemNames.WEB_MODULE_SETTINGS_PERSONAL_DATA_WRITE,
    ]);
    const shownAlready = localStorage.getItem("shownAlready")

    if (isLoggedIn() && !shownAlready && canAccessPreferences) {
        return true;
    }

    return false;
}
